<script setup>
import { WdsButtonGhost, WdsButtonLink, WdsIcon, WdsPopup } from '@wds/components';

const props = defineProps({
  applySheet: {
    type: Boolean,
    required: false,
    default: false,
  },
  totalSocialCues: {
    type: Number,
    required: false,
    default: 0,
  },
});

// EMITS
const emit = defineEmits(['closeSocialCues']);

// COMPOSABLES
const { $tracking } = useNuxtApp();
const { socialCues } = useSocialCues();

// DATA
const showPopup = ref(true);
const getTheAppUrl = 'https://hostelworld.onelink.me/IdiK/gbgvjnnc';

// METHODS
function togglePopup(val) {
  showPopup.value = !!val;
}

function handleGetTheAppClick() {
  $tracking.triggerSocialCuesEventAction({
    action: 'Social Cues CTA Clicked',
    social_cues_available: true,
    social_cues_web_cohort: 'variation',
    social_cues_counter_flag: true,
    social_cues_count_displayed: socialCues.getSocialCuesCount(props.totalSocialCues),
  });
}

function closeSocialCues() {
  emit('closeSocialCues', 'social-cues');
}
</script>

<template>
  <WdsPopup
    class="social-cues-popup"
    :is-open="showPopup"
    :apply-sheet="applySheet"
    close-button
    has-footer
    head-divider
    @popup-close="closeSocialCues"
  >
    <template #popupContent>
      <div
        class="social-cues-title"
        v-html="$t('t_GETMOREWITHAPP')"
      />
      <div class="social-cues-features">
        <div class="social-cues-feature-list web">
          <div class="social-cues-feature title">
            {{ $t('t_AVAILABLEBEFOREBOOK') }}
          </div>
          <div class="social-cues-feature card">
            <WdsIcon
              class="scf-card-icon"
              asset="users"
              width="24"
              height="24"
            />
            <span class="scf-card-title">{{ $t('t_HOWMANYPEOPLETITLE') }}</span>
            <span class="scf-card-descr">{{ $t('t_HOWMANYPEOPLETEXT') }}</span>
          </div>
        </div>
        <div class="social-cues-feature-list app">
          <div class="social-cues-feature title">
            <WdsIcon asset="locker" />
            <span>{{ $t('t_AVAILABLEAFTERBOOK') }}</span>
          </div>
          <div class="social-cues-feature card">
            <WdsIcon
              class="scf-card-icon"
              asset="smile"
              width="24"
              height="24"
            />
            <span class="scf-card-title">{{ $t('t_REVEALPROFILESTITLE') }}</span>
            <span class="scf-card-descr">{{ $t('t_REVEALPROFILESTEXT') }}</span>
          </div>
          <div class="social-cues-feature card">
            <WdsIcon
              class="scf-card-icon"
              asset="chat"
              width="24"
              height="24"
            />
            <span class="scf-card-title">{{ $t('t_UNLOCKCHATTITLE') }}</span>
            <span class="scf-card-descr">{{ $t('t_UNLOCKCHATTEXT') }}</span>
          </div>
          <div class="social-cues-feature card">
            <WdsIcon
              class="scf-card-icon"
              asset="calendar-star"
              width="24"
              height="24"
            />
            <span class="scf-card-title">{{ $t('t_DISCOVEREVENTSTITLE') }}</span>
            <span class="scf-card-descr">{{ $t('t_DISCOVEREVENTSTEXT') }}</span>
          </div>
        </div>
        <div class="social-cues-feature-list more">
          <span class="social-cues-more-title">{{ $t('t_ANDMORE') }}</span>
          <div class="social-cues-feature card">
            <WdsIcon
              class="scf-card-icon"
              asset="reception"
              width="24"
              height="24"
            />
            <span class="scf-card-title">{{ $t('t_MULTILINGUALSUPPORTTITLE') }}</span>
            <span class="scf-card-descr">{{ $t('t_MULTILINGUALSUPPORTTEXT') }}</span>
          </div>
          <div class="social-cues-feature card">
            <WdsIcon
              class="scf-card-icon"
              asset="heart-line"
              width="24"
              height="24"
            />
            <span class="scf-card-title">{{ $t('t_TRIEDANDTRUSTEDTITLE') }}</span>
            <span class="scf-card-descr">{{ $t('t_TRIEDANDTRUSTEDTEXT') }}</span>
          </div>
        </div>
      </div>
    </template>
    <template #popupFooter>
      <div class="social-cues-footer">
        <WdsButtonGhost
          class="social-cues-btn-get-the-app"
          :href="getTheAppUrl"
          :text="$t('t_GETTHEAPP')"
          target="_blank"
          @click="handleGetTheAppClick"
        />
        <WdsButtonLink
          class="social-cues-btn-close"
          :text="$t('t_CLOSE')"
          @click="togglePopup(false)"
        />
      </div>
    </template>
  </WdsPopup>
</template>

<style lang="scss" scoped>
.social-cues-popup {
  .social-cues-title {
    padding: $wds-spacing-m;

    @include title-3-bld;

    :deep(> span) {
      color: $wds-color-purple;
    }
  }

  .social-cues-features {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas:
      'web-features'
      'app-features'
      'more-features';
    gap: $wds-spacing-s;
    padding-inline: $wds-spacing-m;

    .social-cues-feature {
      &.title {
        border-radius: $wds-border-radius-l;
        padding: $wds-spacing-xs $wds-spacing-s;

        @include body-3-bld;
      }

      &.card {
        padding: $wds-spacing-m;
        border-radius: $wds-border-radius-xl;
        display: grid;
        grid-template-columns: auto 1fr;
        grid-template-areas:
          'icon title'
          'icon descr';
        gap: $wds-spacing-s $wds-spacing-m;

        .scf-card-icon {
          grid-area: icon;
          place-self: start left;
          border-radius: 50%;
          padding: $wds-spacing-s;
        }

        .scf-card-title {
          grid-area: title;

          @include body-1-bld;
        }

        .scf-card-descr {
          grid-area: descr;

          @include body-3-reg;
        }
      }
    }

    .social-cues-feature-list {
      display: grid;
      grid-template-rows: min-content auto;
      gap: $wds-spacing-s;

      &.web {
        grid-area: web-features;

        .social-cues-feature.title {
          background-color: $wds-color-purple;
          color: $wds-color-white;
        }

        .social-cues-feature.card {
          background-color: $wds-color-purple-lightest;
          color: $wds-color-ink-darker;

          .scf-card-icon {
            background-color: $wds-color-purple-lighter;
            fill: $wds-color-purple;
          }
        }
      }

      &.app {
        grid-area: app-features;

        .social-cues-feature.title {
          background-color: $wds-color-sunset;
          color: $wds-color-white;
          fill: $wds-color-white;
          display: inline-flex;
          align-items: center;
          gap: $wds-spacing-s;
        }

        .social-cues-feature.card {
          background-color: $wds-color-sunset-lighter;
          color: $wds-color-ink-darker;

          .scf-card-icon {
            background-color: $wds-color-sunset-light;
            fill: $wds-color-white;
          }
        }
      }

      &.more {
        grid-area: more-features;
        margin-top: $wds-spacing-s;
        margin-bottom: $wds-spacing-xl;
        gap: $wds-spacing-m;

        .social-cues-more-title {
          @include title-6-bld;
        }

        .social-cues-feature.card {
          padding: 0;

          .scf-card-icon {
            background-color: $wds-color-purple-lightest;
            fill: $wds-color-purple;
          }

          .scf-card-descr {
            @include body-2-reg;
          }
        }
      }
    }
  }

  .social-cues-footer {
    display: flex;
    flex-direction: column;
    padding-inline: $wds-spacing-m;

    .social-cues-btn-get-the-app,
    .social-cues-btn-close {
      width: 100%;
    }

    .social-cues-btn-close {
      text-decoration: underline;
    }
  }

  @include desktop {
    .social-cues-title {
      @include title-2-bld;
    }

    .social-cues-features {
      grid-template-columns: repeat(4, 1fr);
      grid-template-areas:
        'web-features app-features'
        'more-features more-features';

      .social-cues-feature-list {
        &.web {
          grid-template-columns: 1fr;
        }

        &.app {
          grid-column: 2 / span 3;
          grid-template-columns: auto auto auto;

          .social-cues-feature.title {
            grid-column: 1 / span 3;
          }
        }

        &.more {
          grid-column: 1 / span 4;
          grid-template-columns: repeat(2, 50%);
          gap: $wds-spacing-m $wds-spacing-s;

          .social-cues-more-title {
            grid-column: 1 / span 2;
          }
        }

        .social-cues-feature {
          &.card {
            grid-template-columns: 1fr;
            grid-template-areas:
              'icon'
              'title'
              'descr';
            gap: $wds-spacing-s;
            align-content: start;
          }
        }
      }
    }

    .social-cues-footer {
      flex-direction: row-reverse;
      justify-content: flex-start;
      gap: $wds-spacing-s;

      .social-cues-btn-get-the-app,
      .social-cues-btn-close {
        width: auto;
      }
    }
  }
}
</style>
